import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { Record, List } from "immutable";

import { CLEANING_CHECKLIST_WARNING } from "@config/testIds/checklist";

import { MY_CLEANINGS_MODALS } from "@typings/clientDashboard";
import { Job } from "@typings/jobs";
import { ProfileContract } from "@typings/profileContracts";

import { Modal } from "@ui/Molecules/Modal2";
import { TestWrapper } from "@ui/Atoms";

import { useModalManager } from "@hooks/modal";
import { useTranslate } from "@hooks/useTranslate";
import { useTelemetry } from "@hooks/useTelemetry";

import * as Styled from "./ChecklistWarning.styled";
import { ContractItem } from "./ContractItem";
import { computeUrl, getChecklistParams } from "./ChecklistWarning.helpers";

export const ChecklistWarning = () => {
  const { hideModal, getOption } = useModalManager();
  const history = useHistory();
  const { elementClick } = useTelemetry();
  const translate = useTranslate("batmaid_pwa_profile");
  const [checklistUrl, setChecklistUrl] = useState<string | null>(null);
  const { job, contracts: contractsAll } = getOption<{
    job?: Record<Job>;
    contracts?: List<Record<ProfileContract>>;
  }>(MY_CLEANINGS_MODALS.CHECKLIST_WARNING);

  const contracts = contractsAll?.filter(
    i => i.get("cleaningChecklist").canBeFilled,
  );

  const context = {
    contractUuid:
      (job && job.get("contractUuid")) ||
      (contracts?.size === 1 && contracts.get(0)?.get("contractUuid")),
  };

  const isChecklistWarningTextVisible =
    job ||
    contracts?.some(
      i =>
        i.get("hasSpecialInstructions") &&
        !i.get("cleaningChecklist").isCreated,
    );

  const handleCloseModal = () => {
    elementClick(translate("checklist_warning_modal_cancel_btn"), {
      pageId: "checklist_warning_modal_cancel_btn",
      context,
    });
    hideModal(MY_CLEANINGS_MODALS.CHECKLIST_WARNING);
  };

  useEffect(() => {
    const data = job || contracts?.get(0);
    if (!data) return;

    if (contracts && contracts.size > 1) return;

    const { isCreated, hasSpecialInstructions } = getChecklistParams(data);

    const checklistUrlToRedirect = computeUrl(data);

    if (!hasSpecialInstructions || isCreated) {
      history.push(checklistUrlToRedirect);
      hideModal(MY_CLEANINGS_MODALS.CHECKLIST_WARNING);
    }

    setChecklistUrl(checklistUrlToRedirect);
  }, [job, contracts, history, hideModal]);

  return (
    <Modal
      title={translate("checklist_warning_modal_title")}
      testId={CLEANING_CHECKLIST_WARNING.MODAL}
      cancelText=""
      submitText=""
      onRequestClose={handleCloseModal}
      withContainedHeight
    >
      <Styled.Root>
        {isChecklistWarningTextVisible && (
          <span>{translate("checklist_warning_modal_content")}</span>
        )}
        {contracts && contracts.size > 1 && (
          <Styled.SelectContract>
            <Styled.SelectContractTitle>
              {translate("checklist_warning_modal_contract_selection")}
            </Styled.SelectContractTitle>
            {contracts.map(i => (
              <ContractItem
                key={i.get("contractUuid")}
                contract={i}
                onSelect={contract => {
                  elementClick(
                    translate("checklist_warning_modal_submit_btn"),
                    {
                      pageId: "checklist_warning_modal_submit_btn",
                      context: {
                        contractUuid: contract.get("contractUuid"),
                      },
                    },
                  );
                  history.push(computeUrl(contract));
                  hideModal(MY_CLEANINGS_MODALS.CHECKLIST_WARNING);
                }}
              />
            ))}
          </Styled.SelectContract>
        )}
        <Styled.ButtonsWrapper>
          <TestWrapper testId={CLEANING_CHECKLIST_WARNING.CANCEL_BUTTON}>
            <Styled.CancelButton onClick={handleCloseModal}>
              {translate("batmaid_pwa_generic.button_cancel")}
            </Styled.CancelButton>
          </TestWrapper>
          {checklistUrl && (
            <TestWrapper testId={CLEANING_CHECKLIST_WARNING.GOT_IT_BUTTON}>
              <Styled.ConfirmButton
                onClick={() => {
                  elementClick(
                    translate("checklist_warning_modal_submit_btn"),
                    {
                      pageId: "checklist_warning_modal_submit_btn",
                      context,
                    },
                  );

                  if (checklistUrl) {
                    history.push(checklistUrl);
                    hideModal(MY_CLEANINGS_MODALS.CHECKLIST_WARNING);
                  }
                }}
              >
                {translate("batmaid_pwa_generic.button_got_it")}
              </Styled.ConfirmButton>
            </TestWrapper>
          )}
        </Styled.ButtonsWrapper>
      </Styled.Root>
    </Modal>
  );
};
